import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { durationInMonths } from "@produce/shared";

export interface NewDiscountCode {
  code: string;
  discount_type: string;
  discount_value: number;
  usage_limit: number | null;
  expires_at: string | null;
  duration_type: string;
  duration_months: number | null;
  grants_free_access: boolean;
}

export const EMPTY_DISCOUNT_CODE: NewDiscountCode = {
  code: "",
  discount_type: "percentage",
  discount_value: 10,
  usage_limit: null,
  expires_at: null,
  duration_type: "once",
  duration_months: null,
  grants_free_access: false,
};

/**
 * A free-access code is always a 100% discount — its value isn't editable.
 * Normalising here keeps stored data consistent with how the code is
 * classified (isLifetimeFree / isFreeAccessCode) everywhere else.
 */
export function normaliseDiscountCode(form: NewDiscountCode): NewDiscountCode {
  if (!form.grants_free_access) return form;
  return { ...form, discount_type: "percentage", discount_value: 100 };
}

function periodLabel(form: NewDiscountCode): string {
  const months = durationInMonths(form);
  if (months === null) return "forever";
  return months === 1 ? "1 month" : `${months} months`;
}

/** One-line plain-English summary of what the code will do. */
function summary(form: NewDiscountCode): string {
  if (form.grants_free_access) {
    return form.duration_type === "forever"
      ? "Grants free access forever (no payment method, no charge)."
      : `Grants free access for ${periodLabel(form)}, then access ends — no payment method, no auto-charge.`;
  }
  const value = `${form.discount_value}%`;
  if (form.duration_type === "forever") return `${value} off forever (lifetime).`;
  return `${value} off for ${periodLabel(form)} on a paid subscription (renews after).`;
}

interface Props {
  value: NewDiscountCode;
  onChange: (next: NewDiscountCode) => void;
  onSubmit: () => void;
  isPending: boolean;
}

/**
 * Controlled form for creating a discount code. The parent owns the state,
 * the mutation, and reset-on-success; this component only renders inputs —
 * keeping page/data concerns separate from form presentation.
 */
export function DiscountCodeForm({ value, onChange, onSubmit, isPending }: Props) {
  const set = (patch: Partial<NewDiscountCode>) => onChange({ ...value, ...patch });

  return (
    <div className="space-y-4">
      <div>
        <Label>Code</Label>
        <Input
          placeholder="SUMMER2024"
          value={value.code}
          onChange={(e) => set({ code: e.target.value.toUpperCase() })}
        />
      </div>

      <div className="flex items-center justify-between rounded-md border p-3">
        <div className="pr-4">
          <Label>Free access (no payment required)</Label>
          <p className="mt-1 text-xs text-muted-foreground">
            License valid only for the chosen period. No payment method, no auto-charge — access
            just ends afterwards. Works for once and repeating durations.
          </p>
        </div>
        <Switch
          checked={value.grants_free_access}
          onCheckedChange={(checked) => set({ grants_free_access: checked })}
        />
      </div>

      {!value.grants_free_access && (
        <div>
          <Label>Discount Value (%)</Label>
          <Input
            type="number"
            min="1"
            max="100"
            value={value.discount_value}
            onChange={(e) => set({ discount_value: parseInt(e.target.value) })}
          />
          <p className="mt-1 text-xs text-muted-foreground">
            Applies to LITE, PRO, and STUDIO tiers
          </p>
        </div>
      )}

      <div>
        <Label>Usage Limit (optional)</Label>
        <Input
          type="number"
          min="1"
          placeholder="Unlimited"
          value={value.usage_limit || ""}
          onChange={(e) => set({ usage_limit: e.target.value ? parseInt(e.target.value) : null })}
        />
      </div>

      <div>
        <Label>Duration Type</Label>
        <Select
          value={value.duration_type}
          onValueChange={(v) =>
            set({ duration_type: v, duration_months: v === "repeating" ? 3 : null })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="once">Once (1 month)</SelectItem>
            <SelectItem value="repeating">Repeating (X months)</SelectItem>
            <SelectItem value="forever">Forever (Lifetime)</SelectItem>
          </SelectContent>
        </Select>
        {!value.grants_free_access && value.duration_type !== "forever" && (
          <p className="mt-1 text-xs text-muted-foreground">
            Month-based codes apply to the first invoice — they can only be used on monthly plans,
            not yearly.
          </p>
        )}
      </div>

      {value.duration_type === "repeating" && (
        <div>
          <Label>Duration (months)</Label>
          <Input
            type="number"
            min="1"
            value={value.duration_months || ""}
            onChange={(e) => set({ duration_months: parseInt(e.target.value) })}
          />
        </div>
      )}

      <div>
        <Label>Expires At (optional)</Label>
        <Input
          type="date"
          value={value.expires_at || ""}
          onChange={(e) => set({ expires_at: e.target.value || null })}
        />
      </div>

      <p className="rounded-md bg-muted/50 p-3 text-xs text-muted-foreground">{summary(value)}</p>

      <Button onClick={onSubmit} disabled={isPending || !value.code.trim()} className="w-full">
        Create Code
      </Button>
    </div>
  );
}
